import React, { useState, useEffect } from 'react'
import { subscriptionService } from '../../services/SubscriptionService'

interface OrderHistoryItem {
  orderId: string
  state: string
  price: number
  currency: string
  productDisplayName: string
  createdTime: string
  planName: string
}

interface OrderHistoryModalProps {
  open: boolean
  onClose: () => void
}

/**
 * 自建"查看订单"页面：调用 Casdoor 官方 /api/get-user-orders 查询本人订单/订阅记录
 * 展示：订单号、商品（套餐）、金额、状态、时间
 */
const OrderHistoryModal: React.FC<OrderHistoryModalProps> = ({ open, onClose }) => {
  const [orders, setOrders] = useState<OrderHistoryItem[]>([])
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState('')

  useEffect(() => {
    if (!open) return
    setLoading(true)
    setError('')
    subscriptionService.getUserOrders()
      .then((list) => {
        setOrders(list)
      })
      .catch((e) => {
        console.error('[OrderHistoryModal] 查询订单失败:', e)
        setError('查询订单失败，请稍后重试')
      })
      .finally(() => setLoading(false))
  }, [open])

  if (!open) return null

  const formatTime = (time: string): string => {
    if (!time) return ''
    try {
      const d = new Date(time)
      return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')} ${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}`
    } catch {
      return time
    }
  }

  const getStateLabel = (state: string): { text: string; cls: string } => {
    switch (state) {
      case 'Paid': return { text: '已支付', cls: 'bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-400' }
      case 'Created': return { text: '待支付', cls: 'bg-amber-100 dark:bg-amber-900/30 text-amber-700 dark:text-amber-400' }
      case 'Cancelled': case 'CancelledPayment': return { text: '已取消', cls: 'bg-gray-100 dark:bg-gray-700 text-gray-500 dark:text-gray-400' }
      case 'Failed': return { text: '支付失败', cls: 'bg-red-100 dark:bg-red-900/30 text-red-600 dark:text-red-400' }
      default: return { text: state || '未知', cls: 'bg-gray-100 dark:bg-gray-700 text-gray-500 dark:text-gray-400' }
    }
  }

  const extractPlanName = (displayName: string): string => {
    if (!displayName) return '灵拾笔记会员'
    const raw = displayName.trim()
    // 1. 尝试从 "Product for Plan super-vip/super-vip/Monthly" 提取
    const match = raw.match(/Plan\s+([^/]+)\/([^/]+)/)
    let name = ''
    if (match && match[2]) {
      name = match[2].trim()
    } else {
      name = raw
    }
    // 2. 映射为显示名
    if (name.includes('畅用') || name.includes('畅存')) return name
    if (name.includes('vip 会员') || name === 'vip') return '畅用会员'
    if (name.includes('super-vip') || name === 'super-vip') return '畅存会员'
    return name
  }

  return (
    <div className="fixed inset-0 z-[3000] flex items-center justify-center bg-black/50 backdrop-blur-sm">
      <div className="bg-white dark:bg-gray-900 rounded-2xl shadow-2xl w-full max-w-md mx-4 overflow-hidden animate-fade-in max-h-[85vh] flex flex-col">
        {/* 头部 */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200 dark:border-gray-700">
          <div className="flex items-center gap-2">
            <div className="flex-shrink-0 w-9 h-9 rounded-lg bg-indigo-100 dark:bg-indigo-900/30 flex items-center justify-center">
              <svg className="w-5 h-5 text-indigo-600 dark:text-indigo-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-6 9l2 2 4-4" />
              </svg>
            </div>
            <div>
              <h3 className="text-lg font-bold text-gray-900 dark:text-white">我的订单</h3>
              <p className="text-xs text-gray-500 dark:text-gray-400">订单与订阅记录</p>
            </div>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 p-1"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {/* 订单列表 */}
        <div className="flex-1 overflow-y-auto p-4">
          {loading ? (
            <div className="py-10 text-center">
              <div className="w-10 h-10 border-[3px] border-gray-200 dark:border-gray-700 border-t-blue-500 rounded-full animate-spin mx-auto mb-3" />
              <p className="text-sm text-gray-500 dark:text-gray-400">正在加载订单...</p>
            </div>
          ) : error ? (
            <div className="py-10 text-center">
              <p className="text-sm text-red-500">{error}</p>
            </div>
          ) : orders.length === 0 ? (
            <div className="py-10 text-center">
              <svg className="w-12 h-12 text-gray-300 dark:text-gray-600 mx-auto mb-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
              </svg>
              <p className="text-sm text-gray-500 dark:text-gray-400">暂无订单记录</p>
            </div>
          ) : (
            <div className="space-y-3">
              {orders.map((order) => {
                const stateInfo = getStateLabel(order.state)
                return (
                  <div key={order.orderId} className="bg-gray-50 dark:bg-gray-800/50 rounded-xl p-4">
                    <div className="flex items-center justify-between mb-2">
                      <span className="text-sm font-medium text-gray-900 dark:text-white">
                        {extractPlanName(order.productDisplayName) || '灵拾笔记会员'}
                      </span>
                      <span className={`text-xs px-2 py-0.5 rounded-full ${stateInfo.cls}`}>
                        {stateInfo.text}
                      </span>
                    </div>
                    <div className="flex items-center justify-between mb-1">
                      <span className="text-xs text-gray-400 dark:text-gray-500">订单号</span>
                      <span className="text-xs font-mono text-gray-600 dark:text-gray-300">{order.orderId}</span>
                    </div>
                    <div className="flex items-center justify-between mb-1">
                      <span className="text-xs text-gray-400 dark:text-gray-500">金额</span>
                      <span className="text-sm font-semibold text-gray-900 dark:text-white">
                        {order.currency === 'CNY' ? '¥' : order.currency + ' '}{order.price}
                      </span>
                    </div>
                    {order.createdTime && (
                      <div className="flex items-center justify-between">
                        <span className="text-xs text-gray-400 dark:text-gray-500">时间</span>
                        <span className="text-xs text-gray-600 dark:text-gray-300">{formatTime(order.createdTime)}</span>
                      </div>
                    )}
                  </div>
                )
              })}
            </div>
          )}
        </div>

        {/* 底部关闭 */}
        <div className="p-4 border-t border-gray-200 dark:border-gray-700">
          <button
            type="button"
            onClick={onClose}
            className="w-full py-3 px-4 bg-gray-100 text-gray-700 rounded-xl font-medium hover:bg-gray-200 dark:bg-gray-700 dark:text-gray-300 dark:hover:bg-gray-600 transition-colors"
          >
            关闭
          </button>
        </div>
      </div>
    </div>
  )
}

export default OrderHistoryModal
